'use client'
import {
  type ICustomerDetailsData,
  type State,
  processCheckout
} from '@/app/order/checkout/action'
import ConfirmationPopover from '@/components/cart/confirmation-popover'
import ConcessionList from '@/components/concession/consession-list'
import type { SanityConcessionData } from '@/components/concession/type'
import { ErrorMsg } from '@/components/error-msg'
import MerchList from '@/components/merch/merch-list'
import type { ISanityCheckoutMerch } from '@/components/merch/type'
import { useErrorToast } from '@/components/toastHook'
import { trackEvent } from '@/lib/analytics'
import CheckboxField from '@a24/form/CheckboxField'
import InputField from '@a24/form/InputField'
import Button from '@a24/ui/Button'
import Divider from '@a24/ui/Divider'
import Icon from '@a24/ui/Icon'
import Popover from '@a24/ui/Popover'
import Title from '@a24/ui/Title'
import Link from 'next/link'
import {
  startTransition,
  useActionState,
  useEffect,
  useRef,
  useState
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import featureFlags from '../../featureFlags'
import LoginForm from '../login/login-form'
import BillingAddress from './billing-address'
import CustomerDetailsForm from './customer-details-form'
import PaymentForm from './payment-form'

const initialState = null
const CheckoutForm = ({
  deliveryMethodId,
  defaultValues,
  isAuthenticated,
  concessionData,
  hasItemsInCart,
  orderAmount,
  merch
}: {
  deliveryMethodId: string
  orderAmount: number
  isAuthenticated?: boolean
  defaultValues?: Partial<ICustomerDetailsData>
  concessionData: SanityConcessionData[]
  hasItemsInCart: boolean
  merch: ISanityCheckoutMerch[]
}) => {
  const [showError, setShowError] = useState<boolean>(false)

  const _defaultValues = defaultValues
  if (_defaultValues?.email) {
    _defaultValues.confirm_email = _defaultValues.email
  }
  const formMethods = useForm<ICustomerDetailsData>({
    defaultValues: _defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const [state, formAction, isPending] = useActionState<State, FormData>(
    processCheckout,
    initialState
  )
  const [workaroundAttempts, setWorkaroundAttempts] = useState<number>(1)

  useErrorToast(state)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isAuthenticated) {
      formMethods.setValue('email', defaultValues?.email || '')
      formMethods.setValue('confirm_email', defaultValues?.email || '')
      formMethods.setValue('first_name', defaultValues?.first_name || '')
      formMethods.setValue('last_name', defaultValues?.last_name || '')
    } else {
      formMethods.setValue('email', '')
      formMethods.setValue('confirm_email', '')
      formMethods.setValue('first_name', '')
      formMethods.setValue('last_name', '')
    }
  }, [isAuthenticated])

  const formRef = useRef<HTMLFormElement>(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const confirmPopoverRef = useRef<HTMLDivElement>(null)

  // Workaround for 'Customer already exists in the system.' API validation error
  useEffect(() => {
    if (state?.exception?.number) {
      if (state.exception.number === 5012 && workaroundAttempts < 0) {
        console.warn(
          'Exception 5012 occured [Customer already exists in the system]'
        )
        submitButtonRef.current?.click()
        setWorkaroundAttempts(workaroundAttempts + 1)
        return
      }

      setShowError(true)
    }
  }, [state, workaroundAttempts])

  const handleConfirm = () => {
    console.debug('checkout-form :: handleConfirm -')
    // Don't use state - directly submit since we already validated
    confirmPopoverRef.current?.hidePopover()

    if (formRef.current) {
      startTransition(() => {
        return formAction(new FormData(formRef.current || undefined))
      })
    }
  }

  return (
    <div className='w-full mx-auto mb-24'>
      <ConfirmationPopover ref={confirmPopoverRef} onClick={handleConfirm} />
      <div className='grid gap-10 md:flex md:justify-between w-full items-center'>
        <div>
          <Title className='heading-md mb-6' level='h2' text='General info' />
        </div>
        <Popover
          className={`w-[90%] max-h-[95dvh] min-w-[85vw] top-[50%] translate-y-[-50%] border border-heritage-black-full backdrop:bg-heritage-black-full/20 rounded bg-heritage-white-full left-[50%] translate-x-[-50%] ${isAuthenticated ? 'hidden' : ''}`}
          closeButtonContent={
            <Icon width={24} height={24} id='close-menu-icon' name='close' />
          }
          closeButtonProps={{
            type: 'button',
            className: 'absolute top-0 right-0 z-20 cursor-pointer p-6'
          }}
          buttonProps={{
            type: 'button',
            className: isAuthenticated ? 'hidden' : 'button-secondary h-fit'
          }}
          buttonContent='Login'
        >
          <div className='p-6 relative max-w-4xl mx-auto pb-20 md:pb-32'>
            <Title
              level='h2'
              className='text-center heading-md py-10 md:py-20'
              text='Login'
            />
            <LoginForm />
          </div>
        </Popover>
      </div>
      <Divider className='mt-8 mb-8 md:mt-0' />
      <FormProvider {...formMethods}>
        <form
          ref={formRef}
          onSubmit={async (evt) => {
            evt.preventDefault()

            // Always validate the form first
            const isValid = await formMethods.trigger()

            if (!isValid) {
              // Show validation errors and stop
              return
            }

            // Form is valid, show confirmation popover
            confirmPopoverRef.current?.showPopover()
          }}
        >
          <InputField
            outerWrapperClassName='hidden'
            className='hidden'
            name='deliveryMethodId'
            value={deliveryMethodId}
          />
          <div className='mb-24'>
            <CustomerDetailsForm isAuthenticated={!!isAuthenticated} />
          </div>
          {featureFlags.CONCESSIONS && (
            <ConcessionList concessions={concessionData} />
          )}
          {featureFlags.MERCHANDISE && <MerchList merch={merch} />}
          {orderAmount > 0 && (
            <>
              <PaymentForm />
              <BillingAddress />
            </>
          )}
          <div className='w-fit mb-8'>
            <CheckboxField
              labelClassName='!bg-heritage-white-full !text-heritage-soft-black-full cursor-pointer'
              className='cursor-pointer'
              name='terms'
              label={
                <div>
                  Accept{' '}
                  <Link
                    href='/terms-and-conditions'
                    className='underline'
                    target='_blank'
                  >
                    terms and conditions
                  </Link>
                </div>
              }
              rules={{
                required: 'You must accept the terms and conditions'
              }}
              outerWrapperClassName='mt-10'
            />
          </div>
          {showError && state && <ErrorMsg state={state} />}

          <div className='flex justify-end mt-12'>
            <Button
              ref={submitButtonRef}
              disabled={isPending || !hasItemsInCart}
              aria-disabled={isPending || !hasItemsInCart}
              type='submit'
              className='ml-auto button-secondary mt-5 min-w-[130px]'
              onClick={() =>
                trackEvent({
                  name: 'button_interaction',
                  data: { value: 'Login|Submit' }
                })
              }
            >
              Complete
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default CheckoutForm
